import React, { useState, useEffect } from "react";
import DataTable from "examples/Tables/DataTable";
import CrudService from "services/cruds-service";
import { useNavigate } from "react-router-dom";
import DashboardNavbar from "../../../../../../examples/Navbars/DashboardNavbar";
import DashboardLayout from "../../../../../../examples/LayoutContainers/DashboardLayout";
import {Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend,} from "chart.js";
import Footer from "../../../../../../examples/Footer";
import * as XLSX from "xlsx";
import { appConfirm } from "components/AppDialog";

import { CrmRowActions, ListLink, ListPage, CrmActionButton, CrmNotice, ListTruncate } from "../shared/CrmUi";
import Add from "@mui/icons-material/Add";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend,);
function ServiceProviderTable() {
    const [data, setData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [notification, setNotification] = useState({ value: false, text: "" });
    const navigate = useNavigate();
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await CrudService.getServiceProviders();
                const normalizedData = response.data.map((item) => ({
                    id: item.id,
                    ...item.attributes,
                }));
                setData(normalizedData);
            } catch (error) {
                console.error("Error fetching serviceProviders:", error);
                setNotification({
                    value: true,
                    text: "Failed to load Service Providers. Please try again later.",
                });
            }
        };
        fetchData();
    }, []);
    const [salesRepOptions, setSalesRepOptions] = useState([]);
    useEffect(() => {
        const fetchSalesReps = async () => {
            try {
                const response = await CrudService.getUsers(); // Kullanıcıları çek
                const users = response.data.map((u) => ({
                    id: u.id,
                    name: u.attributes?.name || "Unknown",
                }));
                setSalesRepOptions(users);
            } catch (error) {
                console.error("Error fetching sales reps:", error);
            }
        };
        fetchSalesReps();
    }, []);
    const getRows = (data) => {
        return data.map((serviceProvider) => {
            let salesRepDisplay = "-";
            if (Array.isArray(serviceProvider.salesRep)) {
                const names = serviceProvider.salesRep.map((id) => {
                    const match = salesRepOptions.find((rep) => {
                        const repId = rep.id || rep._id;
                        return repId?.toString() === id?.toString();
                    });
                    return match ? match.name : "Unknown";
                });
                salesRepDisplay = names.join(", ");
            }
            return {
                id: serviceProvider.id,
                serviceProviderNo: serviceProvider.serviceProviderNo,
                name: (
                    <ListTruncate text={serviceProvider.name || "—"} max={40} />),
                serviceProviderCountry: serviceProvider.serviceProviderCountry,
                taxDetails: `${serviceProvider.taxOffice || ""} - ${serviceProvider.taxID || ""}`,
                paymentTerms: serviceProvider.financialInfoByProduct?.[0]?.paymentTerms ?? "-",
                serviceProviderStatus: serviceProvider.serviceProviderStatus,
                shippingPreferences: Array.isArray(serviceProvider.shippingPreferences) ? serviceProvider.shippingPreferences.join(", ") : "",
                actions: (
                    <CrmRowActions onEdit={() => clickEditHandler(serviceProvider.id)} editTitle="Edit Service Provider" onDelete={(e) => clickDeleteHandler(e, serviceProvider.id)} deleteTitle="Delete Service Provider" />
                ),
            };
        });
    };
    useEffect(() => {
        if (data.length > 0 && salesRepOptions.length > 0) {
            setTableData(getRows(data));
        }
    }, [data, selectedRows]);
    const clickEditHandler = (id) => {navigate(`/service-provider/create-service-provider/details?id=${id}`);};
    const clickDeleteHandler = async (e, id) => {
        try {
            if (!await appConfirm("Are you sure you want to delete this Service Provider?")) {
                return;
            }
            await CrudService.deleteServiceProvider(id);
            const updatedData = data.filter((item) => item.id !== id);
            setData(updatedData);
            setNotification({
                value: true,
                text: "Service Provider deleted successfully.",
            });
            setTimeout(() => {
                setNotification({ value: false, text: "" });
            }, 5000);
        } catch (error) {
            console.error("Error deleting Service Provider:", error);
            setNotification({
                value: true,
                text: "Failed to delete Service Provider. Please try again later.",
            });
            setTimeout(() => {
                setNotification({ value: false, text: "" });
            }, 5000);
        }
    };
    useEffect(() => {
        setTableData(getRows(data));
    }, [data]);

    const dataTableData = {
        columns: [
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service Provider ID</span>),
                accessor: "serviceProviderNo",
                Cell: ({ row }) => (
                    <ListLink onClick={() => clickEditHandler(row.original.id)}>
                        {row.original.serviceProviderNo || "No ID"}
                    </ListLink>
                ),
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Service Provider</span>),
                accessor: "name",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Country</span>),
                accessor: "serviceProviderCountry",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Tax Office-ID</span>),
                accessor: "taxDetails",
                Cell: ({ row }) => (
                    <div>
                        <span>{row.original.taxDetails || "No Data"}</span>
                    </div>),
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Payment Terms (days)</span>
                ),
                accessor: "paymentTerms",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Status</span>
                ),
                accessor: "serviceProviderStatus",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Shipping</span>
                ),
                accessor: "shippingPreferences",
            },
            {
                Header: () => (
                    <span style={{ fontWeight: "bold", fontSize: "14px" }}>Actions</span>
                ),
                accessor: "actions",
                disableSortBy: true,
            },
        ],
        rows: tableData,
    };

    const normalizeArray = (val) => {
        if (Array.isArray(val)) return val;
        if (val === null || val === undefined || val === "") return [];
        return [val];
    };

    const toComma = (val) => normalizeArray(val).join(", ");

    const getSalesRepNamesForExport = (salesRepIds) => {
        if (!Array.isArray(salesRepIds)) return "-";
        const names = salesRepIds.map((id) => {
            const match = salesRepOptions.find((rep) => {
                const repId = rep.id || rep._id;
                return repId?.toString() === id?.toString();
            });
            return match ? match.name : id; // isim yoksa id yaz
        });
        return names.join(", ");
    };

// TUM kayitlari cekmek icin (pagination varsa)
    const fetchAllServiceProviders = async () => {
        const all = [];
        let page = 1;
        const pageSize = 200;

        while (true) {
            // CrudService imzasi sende farkli olabilir: buna gore uyarlayabilirsin
            const res = await CrudService.getServiceProviders({ page, pageSize });

            const rows = (res.data || []).map((item) => ({
                id: item.id,
                ...item.attributes,
            }));

            all.push(...rows);

            // meta varsa en saglam kontrol
            const meta = res.meta || res?.data?.meta;
            const pageCount = meta?.pagination?.pageCount;

            const hasNext = pageCount ? page < pageCount : rows.length === pageSize;
            if (!hasNext) break;

            page += 1;
        }

        return all;
    };

    const handleExportExcel = async () => {
        try {
            const allData = await fetchAllServiceProviders();

            const exportRows = allData.map((sp) => ({
                name: sp.name || "",
                serviceProviderCountry: toComma(sp.serviceProviderCountry),
                city: sp.city || "",
                taxID: sp.taxID || "",
                taxOffice: sp.taxOffice || "",
                paymentTerms: sp.financialInfoByProduct?.[0]?.paymentTerms ?? "",
                creditLimit: sp.financialInfoByProduct?.[0]?.creditLimit ?? "",
                serviceProviderStatus: sp.serviceProviderStatus || "",
                shippingPreferences: toComma(sp.shippingPreferences),
                byTypeOfCollaboration: toComma(sp.byTypeOfCollaboration),
                phone: sp.phone || "",
                email: sp.email || "",
            }));


            const ws = XLSX.utils.json_to_sheet(exportRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "ServiceProviders");

            const fileName = `service_providers_all_${new Date().toISOString().slice(0, 10)}.xlsx`;
            XLSX.writeFile(wb, fileName);
        } catch (err) {
            console.error("Export failed:", err);
            setNotification({ value: true, text: "Export failed. Please try again." });
            setTimeout(() => setNotification({ value: false, text: "" }), 5000);
        }
    };



    const clickAddHandler = () => {
        navigate("/service-provider/create-service-provider/details");
    };
    return (
        <DashboardLayout>
            <DashboardNavbar />
            <ListPage
                title="Service Providers"
                subtitle="Carriers, brokers and local partners with their services and contacts. Open a record to edit it."
                actions={
                    <>
                        <CrmActionButton variant="secondary" icon={<FileDownloadOutlined />} onClick={handleExportExcel}>
                            Export
                        </CrmActionButton>
                        <CrmActionButton icon={<Add />} onClick={clickAddHandler}>
                            New Service Provider
                        </CrmActionButton>
                    </>
                }
                notice={notification.value && <CrmNotice tone="blue">{notification.text}</CrmNotice>}
                tableTitle="Service provider list"
                tableSummary={`${dataTableData.rows.length} service providers`}
            >
                <DataTable table={dataTableData} canSearch />
            </ListPage>
            <Footer />
        </DashboardLayout>
    );
}
export default ServiceProviderTable;
